import React from 'react';
import { FileText, Store } from 'lucide-react';
import { findDatenblattDocument } from '../../utils/omdProductView';
import { requireOmdProduct, resolveArticleNumber } from './omdPanelHelpers';
import type { OmdPanelMaterial } from './types';
import { launchIdsArticleDeeplink, pickIdsConnectionForSupplier } from '../../../leads/utils/idsShopLaunch';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';

type OmdPanelFooterProps = {
  product: unknown;
  material: OmdPanelMaterial;
};

const FOOTER_ACTION_CLASS =
  'inline-flex h-9 items-center gap-2 text-sm font-medium text-app-text-muted hover:text-app-text disabled:opacity-40';

export function OmdPanelFooter({ product, material }: OmdPanelFooterProps) {
  const data = requireOmdProduct(product);
  const datenblatt = data ? findDatenblattDocument(data) : null;
  const articleNumber = resolveArticleNumber(material) || String(data?.supplierPid ?? '').trim();
  const supplierSlug = String(material.supplier_slug ?? '').trim().toLowerCase();
  const [shopBusy, setShopBusy] = React.useState(false);
  const [shopError, setShopError] = React.useState<string | null>(null);
  const [shopConnectionId, setShopConnectionId] = React.useState<string | null>(null);
  // Shop-Absprung nur mit IDS-Modul; sonst sperrt der Server /integrations/ids.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const idsAvailable = modulesLoaded && isEnabled('ids');

  React.useEffect(() => {
    if (!articleNumber || !idsAvailable) {
      setShopConnectionId(null);
      return;
    }
    let cancelled = false;
    fetch('/api/integrations/ids/connections', { credentials: 'include' })
      .then((r) => r.json())
      .then((ids) => {
        if (cancelled) return;
        const connections = Array.isArray(ids?.connections) ? ids.connections : [];
        const match = pickIdsConnectionForSupplier(connections, {
          supplierId: material.supplier_id,
          supplierSlug,
        });
        setShopConnectionId(match?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setShopConnectionId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [articleNumber, idsAvailable, material.supplier_id, supplierSlug]);

  const openShop = async () => {
    if (!shopConnectionId) return;
    setShopBusy(true);
    setShopError(null);
    try {
      await launchIdsArticleDeeplink({
        articleNumber,
        connectionId: shopConnectionId,
        supplierId: material.supplier_id ?? undefined,
        supplierSlug: supplierSlug || undefined,
      });
    } catch (err) {
      setShopError(err instanceof Error ? err.message : 'Shop konnte nicht geöffnet werden.');
    } finally {
      setShopBusy(false);
    }
  };

  if (!datenblatt?.url && !shopConnectionId) return null;

  return (
    <div className="shrink-0 border-t border-app-border px-5 py-3 bg-app-surface space-y-2">
      <div className="grid grid-cols-2 items-center">
        <div className="flex justify-center">
          {datenblatt?.url ? (
            <a
              href={datenblatt.url}
              target="_blank"
              rel="noopener noreferrer"
              className={FOOTER_ACTION_CLASS}
            >
              <FileText className="w-4 h-4 shrink-0" />
              <span>Datenblatt öffnen</span>
            </a>
          ) : null}
        </div>
        <div className="flex justify-center">
          {shopConnectionId ? (
            <button type="button" disabled={shopBusy} onClick={() => void openShop()} className={FOOTER_ACTION_CLASS}>
              {shopBusy ? <LoadingSpinner size="sm" /> : <Store className="w-4 h-4 shrink-0" />}
              <span>Im Shop ansehen</span>
            </button>
          ) : null}
        </div>
      </div>
      {shopError ? <p className="text-xs text-red-700 text-right">{shopError}</p> : null}
    </div>
  );
}
